import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PERMISSIONS } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { FormField } from '@/components/ui/FormField';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { useWhatsAppSessionQuery } from '@/modules/whatsapp/api/whatsapp.api';
import { useAutomationQuery, useSaveAutomationMutation, useTemplatesQuery } from '../api/crm.api';

/**
 * The one automation that exists.
 *
 * Deliberately a single card rather than a list of future features: every entry in this catalogue
 * is a trigger somebody implemented and can explain to a customer who asks why they were messaged,
 * and a grid of greyed-out "coming soon" automations would imply a product that does not exist.
 *
 * The distinction the page is built around is **configured** versus **operational**. A shop can
 * switch this on before anybody pairs a phone, and a disconnected phone does not mean the shop
 * changed its mind. Showing the automation as "disabled" because WhatsApp dropped would be telling
 * somebody they had turned something off that they had not.
 */
export function AutomationsPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { branches, activeBranch } = useBranch();

  const branchId = activeBranch?.id ?? branches[0]?.id ?? null;
  const canManage = can(PERMISSIONS.AUTOMATIONS_MANAGE);

  const [scope, setScope] = useState<'tenant' | 'branch'>('tenant');
  const [failure, setFailure] = useState<string | null>(null);

  const scopeBranchId = scope === 'branch' ? branchId : null;
  const automation = useAutomationQuery(scopeBranchId);
  const templates = useTemplatesQuery();
  const session = useWhatsAppSessionQuery(branchId, false);
  const save = useSaveAutomationMutation();

  const readyTemplates = (templates.data?.items ?? []).filter(
    (template) => template.purpose === 'READY_FOR_COLLECTION' && template.isActive,
  );

  const rule = automation.data;
  const active =
    session.data?.activeIdentity === 'ALTERNATIVE'
      ? session.data.alternative
      : session.data?.primary;

  const submit = (next: { enabled: boolean; templateId: string | null }) => {
    setFailure(null);
    save.mutate(
      {
        ...(scopeBranchId ? { branchId: scopeBranchId } : {}),
        enabled: next.enabled,
        templateId: next.templateId,
        version: rule?.version ?? 0,
      },
      {
        onError: (error) => setFailure(apiErrorMessage(error) ?? t('crm.automations.saveFailed')),
      },
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t('crm.automations.title')} subtitle={t('crm.automations.subtitle')} />

      {failure ? <Alert tone="error">{failure}</Alert> : null}

      <Card>
        <CardHeader
          title={t('crm.automations.readyTitle')}
          subtitle={t('crm.automations.readySubtitle')}
          action={
            <Select
              aria-label={t('crm.automations.scope')}
              options={[
                { value: 'tenant', label: t('crm.automations.scopeTenant') },
                { value: 'branch', label: t('crm.automations.scopeBranch') },
              ]}
              value={scope}
              onChange={(event) => setScope(event.target.value as 'tenant' | 'branch')}
            />
          }
        />

        <div className="space-y-4 p-4 pt-0">
          {automation.isPending ? (
            <LoadingState />
          ) : (
            <>
              <dl className="divide-line divide-y">
                <div className="flex items-center justify-between gap-3 py-2">
                  <dt className="text-sm">{t('crm.automations.configured')}</dt>
                  <dd>
                    <Badge tone={rule?.enabled ? 'success' : 'neutral'}>
                      {t(rule?.enabled ? 'crm.automations.enabled' : 'crm.automations.disabled')}
                    </Badge>
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3 py-2">
                  <dt className="text-sm">{t('crm.automations.template')}</dt>
                  <dd className="text-sm">
                    {rule?.templateName ? (
                      <span dir="auto">
                        {rule.templateName}
                        {rule.templateActive ? null : (
                          <Badge tone="warning">{t('crm.automations.templateInactive')}</Badge>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted">{t('crm.automations.noTemplate')}</span>
                    )}
                  </dd>
                </div>
                {/* Separate row, separate question: whether it *can* run right now. */}
                <div className="flex items-center justify-between gap-3 py-2">
                  <dt className="text-sm">{t('crm.automations.operational')}</dt>
                  <dd>
                    <Badge tone={active?.usable ? 'success' : 'warning'}>
                      {t(`crm.whatsappStatus.${active?.status ?? 'NOT_CONFIGURED'}`)}
                    </Badge>
                  </dd>
                </div>
              </dl>

              {rule?.enabled && active && !active.usable ? (
                <Alert tone="warning">
                  <p>{t('crm.automations.enabledButWaiting')}</p>
                  <p className="mt-1">
                    <Link className="text-brand underline" to="/settings/whatsapp">
                      {t('crm.health.openSettings')}
                    </Link>
                  </p>
                </Alert>
              ) : null}

              {canManage ? (
                <div className="space-y-3">
                  <FormField label={t('crm.automations.chooseTemplate')}>
                    {({ id }) => (
                      <Select
                        id={id}
                        options={[
                          { value: '', label: t('crm.automations.noTemplate') },
                          ...readyTemplates.map((template) => ({
                            value: template.id,
                            label: template.name,
                          })),
                        ]}
                        value={rule?.templateId ?? ''}
                        onChange={(event) =>
                          submit({
                            enabled: rule?.enabled ?? false,
                            templateId: event.target.value || null,
                          })
                        }
                      />
                    )}
                  </FormField>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      disabled={save.isPending || !rule?.templateId}
                      onClick={() =>
                        submit({ enabled: true, templateId: rule?.templateId ?? null })
                      }
                    >
                      {t('crm.automations.enable')}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={save.isPending}
                      onClick={() =>
                        submit({ enabled: false, templateId: rule?.templateId ?? null })
                      }
                    >
                      {t('crm.automations.disable')}
                    </Button>
                  </div>
                  <p className="text-muted text-xs">{t('crm.automations.saveBeforePairing')}</p>
                </div>
              ) : (
                <p className="text-muted text-sm">{t('crm.automations.readOnly')}</p>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
